import { useState } from "react";
import { useIntl } from "react-intl";
import { useHistory, useParams } from "react-router-dom";
import BaseModal from "../shared/components/BaseModal";
import BaseButton from "components/common/BaseButton";
import { usAdminAppRoutes } from "route-definitions";
import ErrorPage from "../shared/components/BaseError";
import { useTerritoryManager } from "./hooks/useTerritoryManager";
import useMutateTerritoryManager from "./hooks/useMutateTerritoryManager";
import { TerritoryManagerFormData } from "./components/edit-territory-manager-form/edit-territory-manager.schema";
import EditTerritoryManagerSkeleton from "./components/skeletons/EditTerritoryManagerSkeleton";
import EditTerritoryManagerForm from "./components/edit-territory-manager-form/EditTerritoryManagerForm";
import EntityPageLayout from "../shared/components/EntityPageLayout";

const EditTerritoryManagerPage = () => {
  const intl = useIntl();
  const history = useHistory();

  const [modalIsOpen, setModalIsOpen] = useState(false);

  const { entityId } = useParams<{
    entityId: string;
  }>();

  const { data, isLoading, isError, error, refetch } = useTerritoryManager({
    territoryManagerId: entityId,
  });

  const {
    editTerritoryManager,
    editTerritoryManagerLoading,
    editTerritoryManagerError,
    editTerritoryManagerSuccess,
  } = useMutateTerritoryManager({
    id: entityId,
    onEditSuccess: () => {
      setModalIsOpen(true);
    },
    onEditError() {
      setModalIsOpen(true);
    },
  });

  const onSubmit = (data: TerritoryManagerFormData) => {
    editTerritoryManager({ ...data, territoryManagerId: entityId });
  };

  if (isError)
    return (
      <EntityPageLayout headerMessage="usAdmin.editTerritoryManager">
        <ErrorPage errorMessage={error} onRetry={refetch} />
      </EntityPageLayout>
    );

  if (isLoading)
    return (
      <EntityPageLayout headerMessage="usAdmin.editTerritoryManager">
        <EditTerritoryManagerSkeleton />
      </EntityPageLayout>
    );

  const handleCloseModal = () => {
    setModalIsOpen(false);
    if (editTerritoryManagerSuccess) {
      history.push(usAdminAppRoutes.territoryManagers);
    }
  };

  const territoryManagerError = Array.isArray(
    editTerritoryManagerError?.response?.data?.message
  )
    ? editTerritoryManagerError.response.data.message[0]
    : editTerritoryManagerError?.response?.data?.message;

  return (
    <EntityPageLayout headerMessage="usAdmin.editTerritoryManager">
      <BaseModal
        isOpen={modalIsOpen}
        onRequestClose={handleCloseModal}
        footer={
          <BaseButton
            handleSubmit={handleCloseModal}
            text={intl.formatMessage({ id: "usAdmin.ok" })}
          />
        }
      >
        {editTerritoryManagerSuccess && (
          <h2>
            {intl.formatMessage({
              id: "usAdmin.territoryManagerUpdatedSuccessfully",
            })}
          </h2>
        )}
        {editTerritoryManagerError && <h2>{territoryManagerError}</h2>}
      </BaseModal>
      <EditTerritoryManagerForm
        territoryManager={data!}
        onSubmit={onSubmit}
        isSubmitting={editTerritoryManagerLoading}
      />
    </EntityPageLayout>
  );
};

export default EditTerritoryManagerPage;
